import { Box, Card, Link, MenuItem, Stack, Typography, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Envelope } from '@/api/envelopes';
import { MoneyParts, MoneyText } from '@/components/MoneyText';
import { ProgressTrack, type ProgressTone } from '@/components/ProgressTrack';
import { ReorderHandle, type ReorderHandleProps } from '@/components/SortableCardGrid';
import {
  bpToPercentText,
  formatCivilDateNear,
  formatList,
  formatMoneyMinor,
  formatPercent,
} from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { todayCivilInZone, utcToZonedCivil } from '@/i18n/zonedCivil';
import { useOptionalMe } from '@/session/meHooks';
import { OverflowMenu } from '@/shell/OverflowMenu';
import { destructiveItemSx } from '@/shell/objectMenu';
import { categoricalFor } from '@/theme/categorical';
import { resolveTokens } from '@/theme/theme';
import { BalanceTrend } from './BalanceTrend';
import { SpendingPie } from './SpendingPie';
import { spendingSlices } from './spending';

/**
 * The card's menu actions (TMMAPP-0110). A writer (the owner or a Manager) gets Edit · Archive · Move
 * money · Categories; a Viewer gets only the read actions (Download overview, Alerts). Absent: no kebab.
 */
export interface EnvelopeCardActions {
  /** A writer's (owner or Manager); absent for a Viewer. */
  writer?: {
    onEdit: () => void;
    onArchive: () => void;
    onMoveMoney: () => void;
    /** D1: opens the virtual account's Categories tab. */
    onCategories: () => void;
  };
  /** D1, owner only; absent when there is no other ACTIVE virtual account to move to. */
  onMove?: () => void;
  onDownload: () => void;
  /** D1, owner only (MM-D-47); absent when `aiAnalysisAllowed` is false. */
  onAnalyze?: () => void;
  onAlerts?: () => void;
}

interface EnvelopeCardProps {
  envelope: Envelope;
  virtualAccountId: number;
  /** The virtual account OWNER's currency (§14.1). */
  currency: string | null;
  actions?: EnvelopeCardActions;
  /** The writer's reorder handle and the Move earlier / later items. */
  reorder?: ReorderHandleProps | null;
  /**
   * The meeting session's compact card: name, kind, balance, trend and goal / budget — no menu,
   * no pie, no funded line and no footer.
   */
  compact?: boolean;
}

const CAPTION = { fontSize: 11, lineHeight: '16px' } as const;

/**
 * One envelope in the virtual account's well (TMMAPP-0108; audit 06 § A5 / A6, § Overrides 1; v2
 * L219–298): `surface` with a 3px inline-start border in the envelope's data colour
 * (`categoricalFor(id).line`, the colour its ledger pills, category dots and chart series carry —
 * this supersedes 0099's "no decorative stripe" for this card). The header holds the name (a link to
 * the envelope page) and the kind as text; then the balance at 28/300, the spending pie (EXPENSE
 * only) with its legend, the "Funded {amt} from {category}[, …]" line read from the served
 * `allTimeFlows.inMinor` (never a sum of income categories), the twelve-month trend, the goal /
 * budget progress with the APR line, and "Last activity {date}". A writer's kebab holds
 * Edit · Archive, then Move money…, Categories…, Move to virtual account… (owner), Download
 * overview, Analyze, Alerts and Move earlier / later; a Viewer's holds the read actions only.
 */
export function EnvelopeCard({
  envelope,
  virtualAccountId,
  currency,
  actions,
  reorder = null,
  compact = false,
}: EnvelopeCardProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useOptionalMe();
  const tokens = resolveTokens(useTheme());
  const label = tokens.textLabel('surface');
  const slices = spendingSlices(envelope);
  const money = (minor: number) => formatMoneyMinor(minor, currency, locale);

  const funded = envelope.allTimeFlows?.inMinor ?? 0;
  const fundedFrom = (envelope.allTimeCategoryTotals?.items ?? [])
    .filter((item) => item.kind === 'INCOME' && item.amountMinor > 0)
    .sort((a, b) => b.amountMinor - a.amountMinor)
    .map((item) => item.name);

  const target = envelope.kind !== 'CASH' ? envelope.targetMinor : null;
  const progressBp = envelope.progressBp ?? 0;
  const budget = envelope.kind === 'BUDGET';
  const progressLabel =
    target === null
      ? null
      : t(budget ? 'virtualAccount.card.budget' : 'virtualAccount.card.goal', {
          percent: bpToPercentText(progressBp, locale),
        });
  const progressTone: ProgressTone =
    budget && progressBp > 10000 ? 'over' : !budget && progressBp >= 10000 ? 'reached' : 'progress';
  const zone = me?.user.timezone ?? 'UTC';

  return (
    <Card
      component="article"
      elevation={1}
      data-envelope-card={envelope.id}
      sx={{
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderInlineStart: `3px solid ${categoricalFor(envelope.id, tokens).line}`,
      }}
    >
      {reorder && <ReorderHandle {...reorder} corner />}
      <Stack
        direction="row"
        // A writer's card holds the 24px corner handle (TMMAPP-0097 §D 30) above the name; every
        // card in the grid has it, so the names still start on one line.
        sx={{
          alignItems: 'flex-start',
          gap: 1,
          p: reorder ? '24px 14px 0 18px' : '16px 14px 0 18px',
        }}
      >
        <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
          <Typography variant="h3" component="h3" noWrap sx={{ fontSize: 15, lineHeight: '22px' }}>
            <Link
              component={RouterLink}
              to={`/v/${virtualAccountId}/e/${envelope.id}`}
              underline="none"
              sx={{ color: tokens.text, '&:hover': { color: tokens.link } }}
            >
              {envelope.name}
            </Link>
          </Typography>
          <Typography
            variant="kindLabel"
            component="p"
            data-envelope-kind={envelope.kind}
            sx={{ mt: '3px' }}
          >
            {t(`envelope.kind.${envelope.kind}`)}
          </Typography>
        </Box>
        {actions && !compact && (
          <Box sx={{ m: '-4px -4px 0 0' }}>
            <EnvelopeActionsMenu envelope={envelope} actions={actions} reorder={reorder} />
          </Box>
        )}
      </Stack>

      <Box sx={{ p: '8px 18px 0', lineHeight: 1.15 }}>
        <MoneyText
          minor={envelope.balanceMinor}
          currency={currency}
          variant="figure"
          component="span"
        />
      </Box>

      {!compact && slices.length > 0 && <SpendingPie slices={slices} currency={currency} />}

      {!compact && funded > 0 && (
        <Typography data-funded-line="" sx={{ ...CAPTION, color: tokens.text2, p: '10px 18px 0' }}>
          {fundedFrom.length > 0
            ? t('virtualAccount.card.fundedFrom', {
                amount: money(funded),
                categories: formatList(fundedFrom, locale),
              })
            : t('virtualAccount.card.funded', { amount: money(funded) })}
        </Typography>
      )}

      {envelope.balanceSeries.length > 0 && (
        <Box sx={{ p: '14px 18px 0' }}>
          <BalanceTrend
            series={envelope.balanceSeries}
            currency={currency}
            balanceMinor={envelope.balanceMinor}
          />
        </Box>
      )}

      <Box sx={{ flex: 1 }} />
      {target !== null && progressLabel !== null && (
        <Box sx={{ p: '14px 18px 0' }} data-envelope-progress="">
          <Stack
            direction="row"
            sx={{ justifyContent: 'space-between', alignItems: 'baseline', gap: 1, ...CAPTION }}
          >
            <Box component="span" sx={{ color: tokens.text2, minWidth: 0 }}>
              {progressLabel}
            </Box>
            <Box
              component="span"
              sx={{ color: tokens.text2, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
            >
              <MoneyParts minor={target} currency={currency} whole />
            </Box>
          </Stack>
          {envelope.kind === 'SAVINGS' && envelope.referenceAprBp !== null && (
            <Typography sx={{ ...CAPTION, color: label, mt: '3px' }}>
              {t('virtualAccount.card.apr', {
                apr: formatPercent(envelope.referenceAprBp / 10000, locale, {
                  maximumFractionDigits: 2,
                }),
              })}
            </Typography>
          )}
          <Box sx={{ mt: '5px' }}>
            <ProgressTrack
              value={progressBp / 100}
              tone={progressTone}
              height={4}
              label={`${progressLabel} · ${formatMoneyMinor(target, currency, locale, false, { whole: true })}`}
            />
          </Box>
        </Box>
      )}
      {compact && <Box sx={{ pb: 2 }} />}
      {!compact && (
        <Typography sx={{ ...CAPTION, color: label, p: '14px 18px 16px' }}>
          {envelope.lastActivityAt !== null
            ? t('virtualAccount.card.lastActivity', {
                // This year's date without the year, in the viewer's zone (TMMAPP-0157 item 4).
                date: formatCivilDateNear(
                  utcToZonedCivil(envelope.lastActivityAt, zone).date,
                  locale,
                  todayCivilInZone(zone),
                ),
              })
            : t('virtualAccount.card.noActivity')}
        </Typography>
      )}
    </Card>
  );
}

/**
 * An envelope's kebab (TMMAPP-0110; shared by the meeting session's card and the v2 envelope card,
 * TMMAPP-0151 §F 18): a writer's Edit · Archive · Move money · Categories, then Move to virtual
 * account (owner), Download overview, Analyze, Alerts and Move earlier / later; a Viewer's holds the
 * read actions only.
 */
export function EnvelopeActionsMenu({
  envelope,
  actions,
  reorder,
}: {
  envelope: Envelope;
  actions: EnvelopeCardActions;
  reorder: ReorderHandleProps | null;
}) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  return (
    <OverflowMenu
      variant="card"
      title={envelope.name}
      label={t('envelope.actions', { name: envelope.name })}
    >
      {(close) => {
        const run = (action: () => void) => () => {
          close();
          action();
        };
        const writer = actions.writer;
        return [
          writer && (
            <MenuItem key="edit" onClick={run(writer.onEdit)}>
              {t('envelope.edit')}
            </MenuItem>
          ),
          writer && (
            <MenuItem key="archive" onClick={run(writer.onArchive)} sx={destructiveItemSx(tokens)}>
              {t('envelope.archive')}
            </MenuItem>
          ),
          writer && (
            <MenuItem key="move-money" onClick={run(writer.onMoveMoney)}>
              {t('virtualAccount.card.moveMoney')}
            </MenuItem>
          ),
          writer && (
            <MenuItem key="categories" onClick={run(writer.onCategories)}>
              {t('envelope.categories')}
            </MenuItem>
          ),
          actions.onMove && (
            <MenuItem key="move" onClick={run(actions.onMove)}>
              {t('envelope.moveTo')}
            </MenuItem>
          ),
          <MenuItem key="download" onClick={run(actions.onDownload)}>
            {t('envelope.downloadOverview')}
          </MenuItem>,
          actions.onAnalyze && (
            <MenuItem key="analyze" onClick={run(actions.onAnalyze)}>
              {t('ai.analyze')}
            </MenuItem>
          ),
          actions.onAlerts && (
            <MenuItem key="alerts" onClick={run(actions.onAlerts)}>
              {t('alerts.menu')}
            </MenuItem>
          ),
          reorder && (
            <MenuItem
              key="earlier"
              disabled={!reorder.canMoveEarlier}
              onClick={run(reorder.onMoveEarlier)}
            >
              {t('reorder.moveEarlier')}
            </MenuItem>
          ),
          reorder && (
            <MenuItem
              key="later"
              disabled={!reorder.canMoveLater}
              onClick={run(reorder.onMoveLater)}
            >
              {t('reorder.moveLater')}
            </MenuItem>
          ),
        ];
      }}
    </OverflowMenu>
  );
}
